import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  BasePropertyDTO,
  IBaseDriverPropertyDTO,
  IDispensingPropertiesEntityInfoDTO,
  IDispensingRobotDTO,
} from 'business/models/Models';
import {
  GeneralDispensingProperties,
  IGeneralDispensingPropertiesData,
} from 'components/main/Setup/DispensingProperties/constants';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import { cloneDeep } from 'lodash';
import {
  getGeneralDispensingPropertiesThunk,
  saveGeneralDispensingPropertiesThunk,
} from 'redux/thunks/GeneralDispensingProperties';

const initialState: GeneralDispensingProperties = {
  generalDispensingProperties: {
    robots: Array<IDispensingRobotDTO>(),
    generalProperties: {},
    plateProperties: {},
    liquidClassProperties: {},
    plates: Array<IDispensingPropertiesEntityInfoDTO>(),
    liquidClasses: Array<IDispensingPropertiesEntityInfoDTO>(),
    isFetching: null,
  },
  generalDispensingPropertiesOriginalState: {} as IGeneralDispensingPropertiesData,
  generalDispensingPropertiesToSave: {
    robots: Array<IDispensingRobotDTO>(),
    generalProperties: {},
    plateProperties: {},
    liquidClassProperties: {},
    plates: Array<IDispensingPropertiesEntityInfoDTO>(),
    liquidClasses: Array<IDispensingPropertiesEntityInfoDTO>(),
    isFetching: null,
  },
  isDirty: false,
};
const generalDispensingPropertiesReducer = createSlice({
  name: 'dispensingPropertiesReducer',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(getGeneralDispensingPropertiesThunk.pending, (state) => {
      state.generalDispensingProperties.isFetching = EFetchingStatus.InProgress;
    });
    builder.addCase(getGeneralDispensingPropertiesThunk.fulfilled, (state, action) => {
      state.generalDispensingProperties = {
        ...action.payload,
        isFetching: EFetchingStatus.Loaded,
      } as IGeneralDispensingPropertiesData;
      state.generalDispensingPropertiesOriginalState = cloneDeep(
        state.generalDispensingProperties,
      );
    });
    builder.addCase(getGeneralDispensingPropertiesThunk.rejected, (state, action) => {
      state.generalDispensingProperties.isFetching = EFetchingStatus.NotAvailable;
    });
    builder.addCase(saveGeneralDispensingPropertiesThunk.pending, (state, action) => {
      state.generalDispensingPropertiesToSave.isFetching = EFetchingStatus.InProgress;
    });
    builder.addCase(saveGeneralDispensingPropertiesThunk.fulfilled, (state, action) => {
      if (action.payload) {
        state.generalDispensingPropertiesOriginalState = cloneDeep(
          state.generalDispensingProperties,
        );

        state.generalDispensingPropertiesToSave.generalProperties = {};
        state.generalDispensingPropertiesToSave.plateProperties = {};
        state.generalDispensingPropertiesToSave.liquidClassProperties = {};
        state.isDirty = false;
      }
    });
    builder.addCase(saveGeneralDispensingPropertiesThunk.rejected, (state, action) => {});
  },
  reducers: {
    setAllGeneralDispensingProperties: (
      state,
      action: PayloadAction<GeneralDispensingProperties>,
    ) => {
      return action.payload;
    },
    setChangedDispensingProperties: (
      state,
      action: PayloadAction<IBaseDriverPropertyDTO>,
    ) => {
      const { dispensingRobotId, dispensingPlateId, dispensingLiquidClassId, property } =
        action.payload;
      setProperty(
        dispensingRobotId,
        dispensingPlateId,
        dispensingLiquidClassId,
        state,
        property,
      );
    },
    discard: (state) => {
      state.generalDispensingProperties = cloneDeep(
        state.generalDispensingPropertiesOriginalState,
      );
      state.generalDispensingPropertiesToSave.generalProperties = {};
      state.generalDispensingPropertiesToSave.plateProperties = {};
      state.generalDispensingPropertiesToSave.liquidClassProperties = {};
      state.isDirty = false;
    },
    setIsDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },
  },
});

export const {
  setAllGeneralDispensingProperties,
  setChangedDispensingProperties,
  discard,
  setIsDirty,
} = generalDispensingPropertiesReducer.actions;

export default generalDispensingPropertiesReducer.reducer;

const addProperty = (saveIndex: number, propertiesArray, property: BasePropertyDTO) => {
  if (saveIndex >= 0) {
    propertiesArray[saveIndex] = property;
  } else {
    propertiesArray.push(property);
  }
};

const setProperty = (
  dispensingRobotId: number,
  dispensingPlateId: number,
  dispensingLiquidClassId: number,
  state,
  property: BasePropertyDTO,
) => {
  if (dispensingRobotId && !dispensingPlateId && !dispensingLiquidClassId) {
    const index = state.generalDispensingProperties.generalProperties[
      dispensingRobotId
    ].findIndex((prop) => prop.name === property.name);

    state.generalDispensingProperties.generalProperties[dispensingRobotId][index][
      'value'
    ] = property['value'];

    const generalPropertiesArray =
      (state.generalDispensingPropertiesToSave.generalProperties[dispensingRobotId] ??=
        []);
    const saveIndex = generalPropertiesArray.findIndex(
      (item) => item.name === property.name,
    );

    addProperty(saveIndex, generalPropertiesArray, property);

    state.generalDispensingProperties.generalProperties = {
      ...state.generalDispensingProperties.generalProperties,
    };
  } else if (dispensingRobotId && dispensingPlateId && !dispensingLiquidClassId) {
    const index = state.generalDispensingProperties.plateProperties[dispensingRobotId][
      dispensingPlateId
    ].findIndex((prop) => prop.name === property.name);

    state.generalDispensingProperties.plateProperties[dispensingRobotId][
      dispensingPlateId
    ][index]['value'] = property['value'];

    if (
      !(state.generalDispensingPropertiesToSave.plateProperties[dispensingRobotId] ??=
        {})[dispensingPlateId]
    ) {
      state.generalDispensingPropertiesToSave.plateProperties[dispensingRobotId][
        dispensingPlateId
      ] = [];
      state.generalDispensingPropertiesToSave.plateProperties[dispensingRobotId][
        dispensingPlateId
      ].push(property);
    } else {
      const platePropertiesArray =
        state.generalDispensingPropertiesToSave.plateProperties[dispensingRobotId][
          dispensingPlateId
        ];
      const saveIndex = platePropertiesArray.findIndex(
        (item) => item.name === property.name,
      );

      addProperty(saveIndex, platePropertiesArray, property);
    }

    state.generalDispensingProperties.plateProperties = {
      ...state.generalDispensingProperties.plateProperties,
    };
  } else if (dispensingRobotId && !dispensingPlateId && dispensingLiquidClassId) {
    const index = state.generalDispensingProperties.liquidClassProperties[
      dispensingRobotId
    ][dispensingLiquidClassId].findIndex((prop) => prop.name === property.name);

    state.generalDispensingProperties.liquidClassProperties[dispensingRobotId][
      dispensingLiquidClassId
    ][index]['value'] = property['value'];

    if (
      !(state.generalDispensingPropertiesToSave.liquidClassProperties[
        dispensingRobotId
      ] ??= {})[dispensingLiquidClassId]
    ) {
      state.generalDispensingPropertiesToSave.liquidClassProperties[dispensingRobotId][
        dispensingLiquidClassId
      ] = [];
      state.generalDispensingPropertiesToSave.liquidClassProperties[dispensingRobotId][
        dispensingLiquidClassId
      ].push(property);
    } else {
      const liquidClassPropertiesArray =
        state.generalDispensingPropertiesToSave.liquidClassProperties[dispensingRobotId][
          dispensingLiquidClassId
        ];
      const saveIndex = liquidClassPropertiesArray.findIndex(
        (item) => item.name === property.name,
      );

      addProperty(saveIndex, liquidClassPropertiesArray, property);
    }

    state.generalDispensingProperties.liquidClassProperties = {
      ...state.generalDispensingProperties.liquidClassProperties,
    };
  }
};
